import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Shield, CheckCircle, XCircle, ExternalLink, Clock } from 'lucide-react';

export default function AdminVerifications() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [processing, setProcessing] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const response = await api.get(`/admin/verification-requests${params}`);
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [filterStatus]);

  const handleReview = async (requestId, status) => {
    setProcessing(requestId);
    try {
      await api.put(`/admin/verification-requests/${requestId}`, {
        status,
        admin_notes: adminNotes[requestId] || ''
      });
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <DashboardLayout title="Verification Requests">
      {/* Filter */}
      <div className="flex gap-2 mb-6">
        {['pending', 'approved', 'rejected', ''].map((s) => (
          <button
            key={s || 'all'}
            data-testid={`filter-${s || 'all'}`}
            onClick={() => setFilterStatus(s)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filterStatus === s
                ? 'bg-[#1a1a2e] text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a1a2e]"></div>
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <Shield className="text-neutral-300 mx-auto mb-3" size={40} />
          <p className="text-neutral-500">No verification requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <div key={req.request_id} data-testid={`verification-${req.request_id}`} className="bg-white border border-neutral-200 p-6">
              <div className="flex items-start gap-4">
                {req.researcher?.picture ? (
                  <img src={req.researcher.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-500 font-bold">
                    {req.researcher?.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Link to={`/profile/${req.user_id}`} className="font-semibold hover:text-[#6366f1]">
                      {req.researcher?.name}
                    </Link>
                    <span className={`text-xs px-2 py-0.5 font-medium ${
                      req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      req.status === 'approved' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>{req.status}</span>
                  </div>
                  <p className="text-sm text-neutral-500">{req.profile?.headline || req.researcher?.email}</p>
                  
                  {req.notes && (
                    <p className="text-sm text-neutral-600 bg-neutral-50 p-2 mt-2">{req.notes}</p>
                  )}
                  
                  {req.documents?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {req.documents.map((doc, i) => (
                        <a key={i} href={doc} target="_blank" rel="noopener noreferrer" className="text-xs text-[#6366f1] hover:underline flex items-center gap-1">
                          <ExternalLink size={12} /> Document {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                  
                  {req.linkedin_verified && (
                    <p className="text-xs text-blue-600 mt-1">LinkedIn verified</p>
                  )}

                  {req.status === 'pending' && (
                    <div className="mt-3 space-y-2">
                      <input
                        type="text"
                        data-testid={`admin-notes-${req.request_id}`}
                        placeholder="Admin notes (optional)..."
                        value={adminNotes[req.request_id] || ''}
                        onChange={(e) => setAdminNotes(prev => ({ ...prev, [req.request_id]: e.target.value }))}
                        className="w-full px-3 py-2 border border-neutral-200 text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          data-testid={`approve-${req.request_id}`}
                          onClick={() => handleReview(req.request_id, 'approved')}
                          disabled={processing === req.request_id}
                          className="px-4 py-2 bg-green-600 text-white text-sm font-medium hover:bg-green-700 flex items-center gap-1"
                        >
                          <CheckCircle size={14} /> Approve
                        </button>
                        <button
                          data-testid={`reject-${req.request_id}`}
                          onClick={() => handleReview(req.request_id, 'rejected')}
                          disabled={processing === req.request_id}
                          className="px-4 py-2 border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 flex items-center gap-1"
                        >
                          <XCircle size={14} /> Reject
                        </button>
                      </div>
                    </div>
                  )}

                  {req.admin_notes && (
                    <p className="text-xs text-neutral-500 mt-2">Admin: {req.admin_notes}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
